import * as React from "react";
import { Input, Col, Form, Row, Button, Card, Space, Popconfirm } from "antd";

import { IOrganisation } from "../../../organisations/model/Organisation";
import SelectInput from "../../../core/ui/components/SelectInput";
import { IUser } from "../../../user/model/User";
import { IStore } from "../../../core/model/runtime/state/Store";
import { IDocumentType } from "../../../documentTypes/model/DocumentType";
import { ICourtCase } from "../../../caseLawDocuments/model/CourtCase";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import FormSection from "../../../core/ui/components/FormSection";
import CourtCaseNumberInput from "../../../core/ui/components/CourtCaseNumberInput";
import CaseLawDocumentCourtCaseDescription from "../../../caseLawDocuments/ui/components/CaseLawDocumentCourtCaseDescription";
import CreateModal from "../../../core/ui/components/grid/CreateModal";
import CourtCaseCreationForm from "../../../caseLawDocuments/ui/components/CourtCaseCreationForm";
import { CourtCaseType } from "../../../caseLawDocuments/common/Constants";
import CourtCaseEntryForm from "./../../../caseLawDocuments/ui/components/CourtCaseEntryForm";
import TextArea from "rc-textarea";
import SearchInput from "../../../caseLawDocuments/ui/components/SearchInput";

const FormFields = {
  ID: "id",
  NAME: "name",
  CASE_DECISION_TYPE: "documentTypeId",
  STATUS: "status",
  VALID: "valid",
  ORGANISATION_INDEX_ID: "organisationIndexList",
  ORGANISATION_ID: "organisationId",
  PRIMARY_COURT_CASE: "primaryCourtCase",
  INVALIDATE_PUBLISHED_CASE_LAW_DOCS: "invalidatePublishedCaseLawDocs",
  INVALIDATED_CASE_LAW_DOCUMENTS: "invalidatedCaseLawDocuments",
  INVALIDATION_REASON: "invalidationReason"
};

const CaseDecisionCreationForm = ({
  organisations,
  caseDecisionTypes,
  caseDecisionTypeId,
  setOrganisationId,
  translate,
  onSubmit,
  showCaseDecisionList,
  courtCase,
  findCourtCaseDetails,
  user,
  setCaseDecisionTypeId,
  store,
  courtCaseTypes,
  createCourtCaseModalVisible,
  createCourtCasePopupVisible,
  decisionTypes,
  courtCaseSelectionResult,
  nonExistantCourtCaseNumber,
  uploadedAttachment,
  showCreateCourtCasePopupVisible,
  showCreateCourtCaseModalVisible,
  createCourtCase,
  createAttachment,
  courtCaseConnectionTypeList,
  updateCourtCaseDetailsModalVisibility,
  courtCaseDetailsModalVisible,
  selectCaseDecision,
  updateCourtCaseSelection,
  setInvalidatePublishedCaseDecisions,
  invalidatePublishedCaseDecisionsVisible,
  publishedCaseDecisions,
  selectedOrganisationId,
  onSearch
}: {
  translate: any;
  organisations: IOrganisation[];
  selectedOrganisationId?: number;
  caseDecisionTypes: IDocumentType[];
  caseDecisionTypeId?: number;
  setOrganisationId: (organisationId: number) => void;
  onSubmit;
  showCaseDecisionList: any;
  courtCase?: ICourtCase;
  isCreate: boolean;
  findCourtCaseDetails: any;
  user?: IUser;
  setCaseDecisionTypeId: (id: number) => void;
  store: IStore;
  courtCaseTypes;
  createCourtCaseModalVisible: boolean;
  createCourtCasePopupVisible: boolean;
  decisionTypes;
  courtCaseSelectionResult;
  nonExistantCourtCaseNumber;
  uploadedAttachment;
  showCreateCourtCasePopupVisible: any;
  showCreateCourtCaseModalVisible: any;
  createCourtCase: any;
  createAttachment: any;
  courtCaseConnectionTypeList;
  updateCourtCaseDetailsModalVisibility: any;
  courtCaseDetailsModalVisible?: boolean;
  updateCourtCaseSelection: any;
  selectCaseDecision: any;
  setInvalidatePublishedCaseDecisions: any;
  invalidatePublishedCaseDecisionsVisible: boolean;
  publishedCaseDecisions: any;
  onSearch: (value: string) => void;
}) => {
  const [form] = Form.useForm();
  const [createCourtCaseForm] = Form.useForm();

  function addCourtCaseNumber(caseNumber: string) {
    form.setFieldsValue({ primaryCourtCase: caseNumber });
    findCourtCaseDetails(caseNumber);
  }

  function onChangeOrganisation(item) {
    setOrganisationId(item);
  }

  caseDecisionTypeId &&
    form.setFieldsValue({
      caseDecisionTypeId: caseDecisionTypeId,
      documentTypeId: caseDecisionTypeId
    });
  user &&
    user.isOperator() &&
    form.setFieldsValue({
      organisationId: user.organisationId
    });
  let defaultOrganisation = user?.isOperator()
    ? user.organisationId
    : courtCaseSelectionResult?.caseDecision?.organisationId
    ? courtCaseSelectionResult?.caseDecision?.organisationId
    : selectedOrganisationId || undefined;
  form.setFieldsValue({
    primaryCourtCase: courtCase?.courtCaseNumber,
    organisationId: defaultOrganisation
  });
  return (
    <Form layout={"vertical"} form={form}>
      <Row align="middle" justify="center">
        <Col xl={20} lg={24} md={24} sm={24} xs={24}>
          <Card bordered={false}>
            <Form.Item name={FormFields.ID} style={{ display: "none" }}>
              <Input />
            </Form.Item>
            <Space direction="vertical" style={{ width: "100%" }}>
              <FormSection
                title={translate("editors.case_decisions.basic_data")}
                subtitle={translate("editors.case_decisions.basic_data")}
              >
                <Row gutter={16} style={{ marginBottom: 10, marginTop: 10 }}>
                  <Col xs={24} sm={24}>
                    <div style={{ marginTop: "3em" }}>
                      {!user?.isOperator() && (
                        <Form.Item
                          label={translate(
                            "editors.case_decisions.organisation_name"
                          )}
                          name={FormFields.ORGANISATION_ID}
                          initialValue={defaultOrganisation}
                          rules={[
                            {
                              required: true,
                              message: translate(
                                "editors.case_decisions.required_messages",
                                {
                                  field: translate(
                                    "editors.case_decisions.organisation_name"
                                  )
                                }
                              )
                            }
                          ]}
                        >
                          <SelectInput
                            placeholder={translate(
                              "editors.case_decisions.select_organisation"
                            )}
                            allowClear
                            showSearch={true}
                            notFoundContent={translate("search.no_data")}
                            selectOptionKey={"id"}
                            selectOptionValue={"name"}
                            selectOptions={organisations}
                            onChange={(value) => onChangeOrganisation(value)}
                          />
                        </Form.Item>
                      )}
                      <Form.Item
                        label={translate("editors.case_decisions.case_number")}
                        className={"mb-0"}
                        initialValue={courtCase?.courtCaseNumber}
                        name={FormFields.PRIMARY_COURT_CASE}
                        rules={[
                          {
                            required: true,
                            message: translate(
                              "editors.attachment_types.required_messages",
                              {
                                field: translate(
                                  "editors.case_decisions.case_number"
                                )
                              }
                            )
                          }
                        ]}
                      >
                        <CourtCaseNumberInput
                          onSearch={addCourtCaseNumber}
                          defaultValue={courtCase?.courtCaseNumber}
                        />
                      </Form.Item>
                      {courtCaseSelectionResult && (
                        <Col style={{ marginTop: 20 }}>
                          <CaseLawDocumentCourtCaseDescription
                            courtCase={courtCaseSelectionResult?.courtCase}
                            translate={translate}
                            decisionTypes={decisionTypes}
                          />
                        </Col>
                      )}
                      <CreateModal
                        translate={translate}
                        title={translate(
                          "editors.court_cases.add_new_court_case"
                        )}
                        visible={createCourtCaseModalVisible}
                        onCreate={(item: ICourtCase) => {
                          createCourtCase(item);
                          createCourtCaseForm.resetFields();
                        }}
                        onCancel={() => {
                          createCourtCaseForm.resetFields();
                          showCreateCourtCaseModalVisible(false);
                        }}
                        Form={
                          <CourtCaseCreationForm
                            translate={translate}
                            form={createCourtCaseForm}
                            setFileValue={createAttachment}
                            uploadedAttachment={uploadedAttachment}
                            courtCaseTypes={courtCaseTypes}
                            decisionTypes={decisionTypes}
                          />
                        }
                        formInstance={createCourtCaseForm}
                        className={"modal-width"}
                        rowData={{
                          type: CourtCaseType.PRIMARY,
                          courtCaseNumber: nonExistantCourtCaseNumber ?? ""
                        }}
                      />
                      <Popconfirm
                        title={translate(
                          "editors.court_cases.non_existing_court_case_popconfirm_message"
                        )}
                        visible={createCourtCasePopupVisible}
                        onCancel={() => showCreateCourtCasePopupVisible(false)}
                        onConfirm={() => {
                          showCreateCourtCaseModalVisible(true);
                          showCreateCourtCasePopupVisible(false);
                        }}
                      />
                      <Form.Item
                        label={translate(
                          "editors.case_decisions.case_decision_type"
                        )}
                        name={FormFields.CASE_DECISION_TYPE}
                        style={
                          caseDecisionTypes?.length > 1
                            ? {}
                            : {
                                visibility: "hidden"
                              }
                        }
                        initialValue={undefined}
                        rules={[
                          {
                            required: true,
                            message: translate(
                              "editors.case_decisions.required_messages",
                              {
                                field: translate(
                                  "editors.case_decisions.case_decision_type"
                                )
                              }
                            )
                          }
                        ]}
                      >
                        <SelectInput
                          disabled={
                            form.getFieldValue("organisationId") === undefined
                          }
                          placeholder={translate(
                            "editors.case_decisions.select_case_decision_type"
                          )}
                          allowClear
                          showSearch={true}
                          notFoundContent={translate("search.no_data")}
                          selectOptionKey={"id"}
                          selectOptionValue={"name"}
                          selectOptions={caseDecisionTypes}
                          onChange={(value) => setCaseDecisionTypeId(value)}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </FormSection>
              {invalidatePublishedCaseDecisionsVisible && (
                <div>
                  <Form.Item
                    label={translate(
                      "editors.case_law_documents.case_law_documents_to_be_invalidated"
                    )}
                    className={"mb-0"}
                    name={FormFields.INVALIDATED_CASE_LAW_DOCUMENTS}
                  >
                    <SearchInput
                      mode={"multiple"}
                      data={publishedCaseDecisions}
                      placeholder={translate(
                        "editors.case_law_documents.search_case_law_documents"
                      )}
                      translate={translate}
                      searchAndUpdateData={onSearch}
                      onChange={(value) =>
                        form.setFieldsValue({
                          invalidatedCaseLawDocuments: value
                        })
                      }
                    ></SearchInput>
                  </Form.Item>
                  <Form.Item
                    className={"mb-0"}
                    label={translate(
                      "editors.case_law_documents.invalidation_reason"
                    )}
                    name={FormFields.INVALIDATION_REASON}
                  >
                    <TextArea />
                  </Form.Item>
                </div>
              )}
            </Space>
            <CourtCaseEntryForm
              courtCase={courtCase}
              translate={translate}
              visible={courtCaseDetailsModalVisible || false}
              onHide={() => updateCourtCaseDetailsModalVisibility(false)}
              onSetCourtCaseSelectionResult={updateCourtCaseSelection}
              courtCaseConnectionTypeList={courtCaseConnectionTypeList}
              form={form}
              decisionTypes={decisionTypes}
            />
          </Card>
        </Col>
      </Row>

      <Row justify="center">
        <Col
          lg={20}
          md={24}
          sm={24}
          xs={24}
          style={{ textAlign: "right", paddingTop: 20 }}
        >
          <Button
            type="text"
            danger
            onClick={() => {
              form.resetFields();
              showCaseDecisionList();
            }}
          >
            <CloseOutlined /> {translate("data_grid.modal.actions.cancel")}
          </Button>
          <Button
            type={"primary"}
            onClick={() => {
              form
                .validateFields()
                .then((values) => onSubmit(values))
                .catch((info) => {});
            }}
          >
            <CheckOutlined /> {translate("data_grid.modal.actions.save")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
export default CaseDecisionCreationForm;
